import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  Sparkles, ArrowRight, Loader2, Check, Crown,
  Rocket, Users, LayoutGrid, Zap, Shield, BookOpen,
  Coins, TrendingUp, Plus
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import api, { tokenApi } from '../../services/api';
import { usePricingGrid } from '../../hooks/usePricingGrid';

const UPGRADE_PERKS = [
  { icon: LayoutGrid, label: 'Goals & Initiatives', desc: 'Strategic hierarchy for your product' },
  { icon: Users, label: 'Personas & Ideation', desc: 'User research and idea management' },
  { icon: Zap, label: 'Architect & Delivery', desc: 'Technical architecture and delivery tracking' },
  { icon: Rocket, label: 'Dashboard & Executive AI', desc: 'Org-level analytics and AI insights' },
  { icon: Shield, label: 'Team & Collaboration', desc: 'Invite your team, manage roles' },
  { icon: BookOpen, label: 'Full Documentation', desc: 'Access all help articles and guides' },
];

function formatTokens(n) {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${Math.round(n / 1_000)}K`;
  return String(n);
}

const TokenBalanceCard = ({ productId, colors }) => {
  const [tokenStatus, setTokenStatus] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [statusRes, tiersRes] = await Promise.all([
          tokenApi.getStatus(),
          tokenApi.getTiers(),
        ]);
        setTokenStatus(statusRes.data);
        setTiers(tiersRes.data?.tiers || []);
      } catch (err) {
        console.error('Failed to load token status:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // Confirm top-up on redirect back from Stripe
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    const isTopupReturn = params.get('topup_success') === 'true';
    
    if (!isTopupReturn || !sessionId) return;
    
    const confirm = async () => {
      try {
        const res = await tokenApi.confirmTopup(sessionId);
        const tokens = res.data?.tokens || 0;
        const already = res.data?.already_credited;
        
        if (already) {
          toast.info('This top-up was already applied to your balance.');
        } else {
          toast.success(`+${formatTokens(tokens)} tokens added to your balance!`);
        }
        
        // Refresh token status
        const statusRes = await tokenApi.getStatus();
        setTokenStatus(statusRes.data);
      } catch (err) {
        const detail = err.response?.data?.detail || 'Failed to confirm top-up';
        toast.error(detail);
      }
      
      // Clean URL params
      params.delete('topup_success');
      params.delete('session_id');
      const remaining = params.toString();
      const newUrl = `${window.location.pathname}${remaining ? '?' + remaining : ''}`;
      window.history.replaceState({}, '', newUrl);
    };
    
    confirm();
  }, []);

  const handleTopup = async (tierId) => {
    try {
      setCheckoutLoading(tierId);
      const successUrl = `${window.location.origin}/products/${productId}?tab=upgrade&topup_success=true`;
      const cancelUrl = `${window.location.origin}/products/${productId}?tab=upgrade`;
      
      const res = await tokenApi.checkoutTopup(tierId, successUrl, cancelUrl);
      
      if (res.data?.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to start checkout';
      toast.error(msg);
    } finally {
      setCheckoutLoading(null);
    }
  };

  if (loading) {
    return (
      <Card className={cn(colors.bgCard, colors.borderPrimary, "mb-8")}>
        <div className="p-6 flex items-center justify-center">
          <Loader2 className={cn("w-5 h-5 animate-spin", colors.textMuted)} />
        </div>
      </Card>
    );
  }

  const budget = tokenStatus?.budget || 0;
  const used = tokenStatus?.used || 0;
  const remaining = tokenStatus?.remaining || 0;
  const percentUsed = budget > 0 ? Math.min((used / budget) * 100, 100) : 0;

  // Color of the usage bar
  const barColor = percentUsed > 90
    ? 'bg-red-500'
    : percentUsed > 70
      ? 'bg-amber-500'
      : 'bg-emerald-500';

  return (
    <Card className={cn(colors.bgCard, "border-amber-500/30 mb-8")} data-testid="token-balance-card">
      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="p-1.5 rounded-md bg-amber-500/10">
              <Coins className="w-4 h-4 text-amber-400" />
            </div>
            <div>
              <h3 className={cn("text-base font-semibold", colors.textPrimary)}>AI Token Balance</h3>
              <p className={cn("text-xs", colors.textMuted)}>Tokens power all Co-Pilot and AI features</p>
            </div>
          </div>
          <div className="text-right">
            <p className={cn("text-2xl font-bold", colors.textPrimary)} data-testid="token-remaining">
              {formatTokens(remaining)}
            </p>
            <p className={cn("text-xs", colors.textMuted)}>remaining</p>
          </div>
        </div>

        {/* Usage Bar */}
        <div className={cn("w-full h-2 rounded-full mb-2", colors.bgSecondary)} data-testid="token-usage-bar">
          <div
            className={cn("h-full rounded-full transition-all duration-500", barColor)}
            style={{ width: `${percentUsed}%` }}
          />
        </div>
        <div className="flex justify-between text-xs mb-5">
          <span className={colors.textMuted}>{formatTokens(used)} used</span>
          <span className={colors.textMuted}>{formatTokens(budget)} budget</span>
        </div>

        {/* Top-up Tiers */}
        <div className="flex items-center gap-2 mb-3">
          <TrendingUp className="w-3.5 h-3.5 text-amber-400" />
          <span className={cn("text-xs font-semibold uppercase tracking-wider", "text-amber-400")}>
            Buy More Tokens
          </span>
        </div>
        <div className="grid grid-cols-3 gap-3">
          {tiers.map((tier) => (
            <button
              key={tier.tier_id}
              onClick={() => handleTopup(tier.tier_id)}
              disabled={checkoutLoading === tier.tier_id}
              className={cn(
                "relative flex flex-col items-center p-3 rounded-lg border transition-all",
                "hover:border-amber-500/50 hover:bg-amber-500/5",
                colors.bgSecondary, colors.borderPrimary,
                checkoutLoading === tier.tier_id && "opacity-60"
              )}
              data-testid={`topup-tier-${tier.tier_id}`}
            >
              {checkoutLoading === tier.tier_id ? (
                <Loader2 className="w-4 h-4 animate-spin text-amber-400 mb-1" />
              ) : (
                <Plus className="w-4 h-4 text-amber-400 mb-1" />
              )}
              <span className={cn("text-sm font-bold", colors.textPrimary)}>{tier.label}</span>
              <span className={cn("text-xs font-medium text-amber-400")}>
                ${(tier.price_cents / 100).toFixed(2)}
              </span>
              <span className={cn("text-[10px]", colors.textMuted)}>one-time</span>
            </button>
          ))}
        </div>
      </div>
    </Card>
  );
};

const UpgradeTab = ({ productId, colors }) => {
  const { loading: plansLoading, getPaidPlans, getEnterprisePlan } = usePricingGrid();
  const [permLoading, setPermLoading] = useState(true);
  const [canUpgrade, setCanUpgrade] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(null);

  const loadPermissions = useCallback(async () => {
    try {
      const res = await api.get('/api/billing/upgrade-context?limit_type=products');
      setCanUpgrade(res.data?.can_upgrade ?? false);
    } catch (err) {
      console.error('Failed to load upgrade permissions:', err);
    } finally {
      setPermLoading(false);
    }
  }, []);

  useEffect(() => { loadPermissions(); }, [loadPermissions]);

  const handleUpgrade = async (planId) => {
    try {
      setCheckoutLoading(planId);
      const successUrl = `${window.location.origin}/products/${productId}?upgraded=true`;
      const cancelUrl = `${window.location.origin}/products/${productId}?tab=upgrade`;
      
      const res = await api.post('/api/stripe/create-checkout-session', {
        plan_id: planId,
        seats: 1,
        success_url: successUrl,
        cancel_url: cancelUrl,
      });
      
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else if (res.data.subscription_created) {
        toast.success('Upgrade complete! Refreshing...');
        window.location.reload();
      }
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to start checkout';
      toast.error(msg);
    } finally {
      setCheckoutLoading(null);
    }
  };

  const handleContactSales = (planId) => {
    window.open(`mailto:sales@stackranked.ai?subject=Enterprise%20Inquiry&body=I'm%20interested%20in%20the%20Enterprise%20plan%20(${planId})`, '_blank');
  };

  const loading = plansLoading || permLoading;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className={cn("w-8 h-8 animate-spin", colors.textMuted)} />
      </div>
    );
  }

  const paidPlans = getPaidPlans();
  const enterprisePlan = getEnterprisePlan();
  const plans = [...paidPlans, ...(enterprisePlan ? [enterprisePlan] : [])];

  return (
    <div className="max-w-5xl mx-auto py-8 px-4" data-testid="upgrade-tab">
      {/* Token Balance — Free Tier Only */}
      <TokenBalanceCard productId={productId} colors={colors} />

      {/* Header */}
      <div className="text-center mb-10">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/20 mb-4">
          <Sparkles className="w-4 h-4 text-amber-400" />
          <span className="text-sm text-amber-400 font-medium">Unlock Full Power</span>
        </div>
        <h2 className={cn("text-2xl font-bold mb-2", colors.textPrimary)}>
          Ready to go beyond exploring?
        </h2>
        <p className={cn("text-sm max-w-lg mx-auto", colors.textMuted)}>
          You've built something great. Upgrade to unlock the complete StackRanked experience — 
          strategy, team collaboration, delivery tracking, and more.
        </p>
      </div>

      {/* What you unlock */}
      <div className="mb-10">
        <h3 className={cn("text-sm font-semibold uppercase tracking-wider mb-4 text-center", colors.textMuted)}>
          What you unlock
        </h3>
        <div className="grid grid-cols-3 gap-3">
          {UPGRADE_PERKS.map(({ icon: Icon, label, desc }) => (
            <div
              key={label}
              className={cn(
                "flex items-start gap-3 p-3 rounded-lg border",
                colors.bgSecondary, colors.borderPrimary
              )}
            >
              <div className="p-1.5 rounded-md bg-amber-500/10 mt-0.5">
                <Icon className="w-4 h-4 text-amber-400" />
              </div>
              <div>
                <p className={cn("text-sm font-medium", colors.textPrimary)}>{label}</p>
                <p className={cn("text-xs", colors.textMuted)}>{desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Plans */}
      {plans.length > 0 ? (
        <div className={`grid ${plans.length === 1 ? 'grid-cols-1 max-w-md mx-auto' : plans.length === 2 ? 'grid-cols-2 max-w-3xl mx-auto' : 'grid-cols-3'} gap-6 items-stretch`}>
          {plans.map((plan) => {
            const isPopular = plan.display?.show_popular_badge;
            const priceCents = plan.price_cents || 0;
            const priceDisplay = priceCents > 0 ? `$${(priceCents / 100).toFixed(0)}` : 'Custom';
            const isInquire = plan.plan_type === 'inquire';
            const featureBullets = plan.display?.feature_bullets || [];

            return (
              <Card
                key={plan.plan_id}
                className={cn(
                  "relative transition-all flex flex-col",
                  isPopular
                    ? "border-amber-500/50 bg-amber-500/5"
                    : cn(colors.bgCard, colors.borderPrimary)
                )}
                data-testid={`upgrade-plan-${plan.plan_id}`}
              >
                {isPopular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge className="bg-amber-500 text-black text-xs px-3 py-0.5">
                      <Crown className="w-3 h-3 mr-1" />
                      Recommended
                    </Badge>
                  </div>
                )}
                <div className="p-6 flex flex-col flex-1">
                  <h3 className={cn("text-lg font-bold mb-1", colors.textPrimary)}>
                    {plan.label}
                  </h3>
                  <div className="flex items-baseline gap-1 mb-2">
                    <span className={cn("text-3xl font-bold", colors.textPrimary)}>
                      {priceDisplay}
                    </span>
                    {priceCents > 0 && (
                      <span className={cn("text-sm", colors.textMuted)}>/mo per seat</span>
                    )}
                  </div>

                  {plan.display?.tagline && (
                    <p className={cn("text-xs mb-4", colors.textMuted)}>{plan.display.tagline}</p>
                  )}

                  {featureBullets.length > 0 && (
                    <div className={cn("text-xs space-y-2 mb-4 pb-4 border-b flex-1", colors.borderPrimary, colors.textMuted)}>
                      {featureBullets.map((bullet, idx) => (
                        <div key={idx} className="flex items-start gap-1.5">
                          <Check className="w-3 h-3 text-green-500 mt-0.5 flex-shrink-0" />
                          <span>{bullet}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {canUpgrade && (
                    <div className="mt-auto">
                    {isInquire ? (
                      <Button
                        variant="outline"
                        className="w-full"
                        onClick={() => handleContactSales(plan.plan_id)}
                        data-testid={`upgrade-contact-${plan.plan_id}`}
                      >
                        Contact Sales
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    ) : (
                      <Button
                        className={cn(
                          "w-full",
                          isPopular
                            ? "bg-amber-500 hover:bg-amber-600 text-black"
                            : ""
                        )}
                        onClick={() => handleUpgrade(plan.plan_id)}
                        disabled={checkoutLoading === plan.plan_id}
                        data-testid={`upgrade-checkout-${plan.plan_id}`}
                      >
                        {checkoutLoading === plan.plan_id ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Rocket className="w-4 h-4 mr-2" />
                        )}
                        Upgrade Now
                      </Button>
                    )}
                    </div>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      ) : (
        <div className={cn("text-center py-12", colors.textMuted)}>
          <p>No upgrade plans available at this time.</p>
        </div>
      )}
    </div>
  );
};

export default UpgradeTab;
